import { useState } from 'react'
import AgeGroupSelector from '../../components/AgeGroupSelector'
import MazeCanvas from './MazeCanvas'
import { generateMaze } from '../../lib/maze/generator'
import { AgeGroup, MazeConfig, MazePuzzle, MazeSize, MAZE_DIMENSIONS, MAZE_SIZE_DEFAULTS } from '../../types'

// ─── Config ───────────────────────────────────────────────────────────────────

const defaultConfig: MazeConfig = {
  ageGroup: 'young',
  size:     'medium',
}

type SizeOption = { value: MazeSize; label: string; sub: string }

const SIZES: SizeOption[] = [
  { value: 'small',  label: 'Small',  sub: '10×10 grid' },
  { value: 'medium', label: 'Medium', sub: '15×15 grid' },
  { value: 'large',  label: 'Large',  sub: '20×20 grid' },
]

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function MazePage() {
  const [config, setConfig]         = useState<MazeConfig>(defaultConfig)
  const [maze, setMaze]             = useState<MazePuzzle | null>(null)
  const [showSolution, setShowSolution] = useState(false)
  const [solved, setSolved]         = useState(false)
  const [generating, setGenerating]       = useState(false)
  const [confirmReveal, setConfirmReveal] = useState(false)
  const [withSolution, setWithSolution]     = useState(false)
  const [printing, setPrinting]             = useState(false)
  const [worksheetCount, setWorksheetCount] = useState(1)

  async function handlePrint() {
    if (!maze) return
    setPrinting(true)
    try {
      const { createElement }     = await import('react')
      const { downloadPDF }       = await import('../../lib/pdf/download')
      const { MazeDocument }      = await import('../../lib/pdf/maze')
      const { generateMaze: gen } = await import('../../lib/maze/generator')
      const mazes = [
        maze,
        ...Array.from({ length: worksheetCount - 1 }, () => gen(config)),
      ]
      const filename = worksheetCount > 1
        ? `maze-worksheet-${worksheetCount}x-${config.size}.pdf`
        : `maze-${config.size}.pdf`
      await downloadPDF(createElement(MazeDocument, { mazes, withSolution }), filename)
    } finally {
      setPrinting(false)
    }
  }

  // ── Handlers ──────────────────────────────────────────────────────────

  function handleStart(cfg: MazeConfig) {
    setGenerating(true)
    setTimeout(() => {
      setMaze(generateMaze(cfg))
      setSolved(false)
      setShowSolution(false)
      setConfirmReveal(false)
      setGenerating(false)
    }, 20)
  }

  function handleNewMaze() {
    if (maze) handleStart(config)
  }

  function handleAgeGroupChange(ageGroup: AgeGroup) {
    setConfig({ ageGroup, size: MAZE_SIZE_DEFAULTS[ageGroup] })
  }

  // ── Screens ───────────────────────────────────────────────────────────

  if (!maze) {
    return (
      <ConfigScreen
        config={config}
        generating={generating}
        onAgeGroupChange={handleAgeGroupChange}
        onSizeChange={(size) => setConfig((p) => ({ ...p, size }))}
        onStart={() => handleStart(config)}
      />
    )
  }

  const { rows, cols } = MAZE_DIMENSIONS[config.size as MazeSize]

  return (
    <div className="max-w-xl mx-auto">
      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display font-black text-2xl text-bubble-text">Maze</h1>
          <p className="text-sm text-bubble-muted capitalize">
            {config.size} — {rows}×{cols} grid
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleNewMaze}
            disabled={generating}
            className="btn-secondary text-sm px-4 py-2"
          >
            {generating ? '⏳' : '↺'} New
          </button>
          <label className="flex items-center gap-1 text-xs text-bubble-muted font-black cursor-pointer select-none">
            <input
              type="checkbox"
              checked={withSolution}
              onChange={(e) => setWithSolution(e.target.checked)}
              className="accent-bubble-coral"
            />
            +solution
          </label>
          <select
            value={worksheetCount}
            onChange={(e) => setWorksheetCount(Number(e.target.value))}
            className="text-xs font-black text-bubble-text border-2 border-gray-200 rounded-full px-3 py-1.5 bg-white cursor-pointer focus:outline-none focus:border-bubble-coral"
            aria-label="Number of mazes to print"
          >
            {[1, 2, 3, 5, 10].map((n) => (
              <option key={n} value={n}>{n === 1 ? '1 maze' : `${n} mazes`}</option>
            ))}
          </select>
          <button
            onClick={handlePrint}
            disabled={printing}
            className="btn-primary text-sm px-4 py-2 disabled:opacity-60"
          >
            {printing ? '⏳ Generating…' : `🖨 ${worksheetCount > 1 ? 'Worksheet' : 'Print'}`}
          </button>
        </div>
      </div>

      {/* ── Solved banner ── */}
      {solved && !showSolution && (
        <div className="card border-bubble-teal bg-bubble-teal-light mb-5 p-4 text-center">
          <p className="font-display font-black text-xl text-teal-700 mb-1">
            🎉 You found the way out!
          </p>
          <p className="text-sm text-teal-600">Try a bigger maze?</p>
        </div>
      )}

      {/* ── Maze canvas ── */}
      <MazeCanvas
        maze={maze}
        showSolution={showSolution}
        onSolved={() => setSolved(true)}
      />

      {/* ── Show solution controls ── */}
      {!solved && (
        <div className="flex gap-3 mt-5 justify-center">
          {!confirmReveal ? (
            <button
              onClick={() => setConfirmReveal(true)}
              className="btn-secondary px-6 py-2.5 text-sm"
            >
              Show solution
            </button>
          ) : (
            <div className="flex gap-2 items-center flex-wrap justify-center">
              <span className="text-xs text-bubble-muted font-black">Show the solution?</span>
              <button
                onClick={() => { setShowSolution(true); setConfirmReveal(false) }}
                className="btn-secondary px-4 py-2 text-xs"
              >
                Yes, reveal
              </button>
              <button
                onClick={() => setConfirmReveal(false)}
                className="btn-ghost px-3 py-2 text-xs"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {showSolution && (
        <div className="mt-3 text-center">
          <p className="text-xs text-bubble-muted font-black">
            Solution shown in purple · your path in coral
          </p>
        </div>
      )}
    </div>
  )
}

// ─── Config screen ────────────────────────────────────────────────────────────

interface ConfigScreenProps {
  config:          MazeConfig
  generating:      boolean
  onAgeGroupChange: (v: AgeGroup) => void
  onSizeChange:    (v: MazeSize) => void
  onStart:         () => void
}

function ConfigScreen({
  config,
  generating,
  onAgeGroupChange,
  onSizeChange,
  onStart,
}: ConfigScreenProps) {
  return (
    <div className="max-w-lg">
      <h1 className="font-display font-black text-3xl text-bubble-text mb-1">Maze</h1>
      <p className="text-bubble-muted mb-8">Find the path from START to EXIT.</p>

      <div className="card p-6 space-y-6">
        <AgeGroupSelector value={config.ageGroup} onChange={onAgeGroupChange} />

        <div>
          <label className="config-label">Size</label>
          <div className="flex gap-2" role="radiogroup" aria-label="Maze size">
            {SIZES.map(({ value, label, sub }) => (
              <button
                key={value}
                role="radio"
                aria-checked={config.size === value}
                onClick={() => onSizeChange(value)}
                className={config.size === value ? 'sel-btn-active' : 'sel-btn'}
              >
                <span>{label}</span>
                <span className="text-xs font-normal text-bubble-muted mt-0.5">{sub}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-[16px] bg-bubble-mint-light border-2 border-bubble-mint p-3 text-sm text-center">
          <p className="font-black text-green-800 mb-0.5">How to play</p>
          <p className="text-green-700 text-xs">
            Click or drag from any cell to trace your path. You can't cross walls.
            Reach EXIT to win!
          </p>
        </div>

        <button
          onClick={onStart}
          disabled={generating}
          className="btn-primary w-full text-base py-3 disabled:opacity-50"
        >
          {generating ? 'Generating…' : 'Generate Maze →'}
        </button>
      </div>
    </div>
  )
}
